import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIsMobile } from '../../hooks/useIsMobile';
import Content from './Content';
import {
    requestToUpdateNotificationsAsViewedData,
    TOGGLE_NOTIFICATION_FEED,
} from '../../ducks/UI.duck';
import { CustomNotificationContext } from './context';
import {
    getCustomNotificationViewedItems,
    setCustomNotificationViewedItems,
} from '../../util/localStorage';

const UserCustomNotificationBadge = ({ renderCustomElement }) => {
    const dispatch = useDispatch();
    const toggleNotificationFeed = payload => dispatch({ type: TOGGLE_NOTIFICATION_FEED, payload });

    const viewedNotifications = useRef({});

    const [isMobile, , computing] = useIsMobile();

    const currentUser = useSelector(state => state['user'].currentUser);
    const inProgress = useSelector(s => s['UI'].userCustomNotificationsRequestInProgress);
    const userCustomNotifications = useSelector(s => s['UI'].userCustomNotifications);
    const notificationsRequestFailed = useSelector(
        s => s['UI'].userCustomNotificationsRequestFailed
    );
    const notificationsFeedOpen = useSelector(s => s['UI'].notificationFeedPopupOpen);
    const isAuthorized = Boolean(currentUser?.id?.uuid);
    const viewedAsNonAuthed = getCustomNotificationViewedItems();

    const updateStorageNotification = async () => {
        const localKeys = Object.keys(viewedNotifications?.current || {});

        if (localKeys.length === 0) return;

        if (isAuthorized) {
            await dispatch(requestToUpdateNotificationsAsViewedData(viewedNotifications?.current));
        } else {
            setCustomNotificationViewedItems(
                localKeys.reduce((acc, key) => ({ ...acc, [key]: { viewedAt: null } }), {})
            );
        }

        viewedNotifications.current = {};
    };

    const markNotificationsAsViewed = async notifiction => {
        viewedNotifications.current = {
            ...viewedNotifications.current,
            [notifiction.id || notifiction._id]: true, // or any other positive value
        };

        await updateStorageNotification();
    };

    /**
     * Non-authed to authed flow
     *
     * A user hasn't viewed notifications yet:
     * - no localStorage value exists;
     * - a notification is considered to be "new";
     * A user viewed a notification:
     * - localStorage key is added;
     * - localStorage value is set to viewedAt with an empty value;
     * A user logs in:
     * - localStorage value persists;
     * - if localStorage viewedAt value is empty, a user data updated;
     * - if localStorage viewedAt value is non-empty, no need to update;
     */
    const checkIfNotificationIsNew = ({ viewedAt, id }) =>
        isAuthorized
            ? !viewedAt && !viewedAsNonAuthed.hasOwnProperty(id)
            : !viewedAsNonAuthed.hasOwnProperty(id);

    useEffect(() => {
        if (notificationsFeedOpen) return;
        if (typeof notificationsFeedOpen !== 'boolean') return; // initial state, no opened yet

        updateStorageNotification();
    }, [notificationsFeedOpen]);

    const popupOpen = notificationsFeedOpen && !isMobile;
    const modalOpen = notificationsFeedOpen && isMobile;

    const hasNewNotifications = userCustomNotifications.some(checkIfNotificationIsNew);

    return (
        <CustomNotificationContext.Provider
            value={{
                inProgress,
                hasNewNotifications,
                userCustomNotifications,
                notificationsRequestFailed,
                viewedAsNonAuthed,
                popupOpen,
                modalOpen,
                isMobile,
                computing,
                open: notificationsFeedOpen,
                setOpen: toggleNotificationFeed,
                markNotificationsAsViewed,
                checkIfNotificationIsNew,
            }}
        >
            <Content renderCustomElement={renderCustomElement} />
        </CustomNotificationContext.Provider>
    );
};

export default UserCustomNotificationBadge;
